import { useEffect, useState } from "react";

import { Column, ColumnDef, Row } from "@tanstack/react-table";
import { MoreHorizontalIcon, Plus } from "lucide-react";
import {
  parseAsInteger,
  parseAsJson,
  parseAsString,
  useQueryState,
} from "nuqs";
import { z } from "zod";

import NoParticipantsSvg from "@/../../hypetrain/static/img/program/no-participants.svg";
import { DataTable } from "@/components/data-table/data-table";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { DataTableToolbar } from "@/components/data-table/data-table-toolbar";
import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import {
  isProgramLead,
  isWorkspaceAdmin,
} from "@/components/helpers/workspace/permissions";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { ProgramUserRole } from "@/graphql";
import { useDataTable } from "@/hooks/use-data-table";
import { MainProgram, ParticipantTableField } from "@/types/programs";
import { HeaderConfig } from "@/types/tables";

import { Participant, renderUserCell } from "./participant-table-utils";

export interface ParticipantHeaderConfig extends HeaderConfig {
  field: ParticipantTableField;
}

interface ProgramParticipantsTableProps {
  loading?: boolean;
  participants: Participant[];
  program: MainProgram;
  onSort: (field: ParticipantTableField, desc?: boolean) => void;
  onRemoveParticipant?: (participant?: Participant) => void;
  onAddParticipants?: () => void;
  query?: string;
  totalRecords: number;
  pageCount: number;
  onPaginationChange: (page: number, size: number) => void;
  hasPendingUsers: boolean;
  onSearch: (value: string) => void;
}

export function ProgramParticipantsTable({
  loading = false,
  participants,
  onSort,
  onRemoveParticipant,
  program,
  onAddParticipants,
  query,
  totalRecords,
  onPaginationChange,
  pageCount,
  hasPendingUsers,
  onSearch,
}: ProgramParticipantsTableProps) {
  const isAdmin = isWorkspaceAdmin();
  const isLead = isProgramLead(program.id);
  const isProgramClosed = Boolean(program?.closedAt);
  const sortSchema = z.array(
    z.object({
      id: z.string(),
      desc: z.boolean(),
    }),
  );
  const [_sort] = useQueryState("sort", parseAsJson(sortSchema.parse));
  useQueryState("page", parseAsInteger);
  const [page] = useQueryState("page", parseAsInteger);
  const [size] = useQueryState("perPage", parseAsInteger);
  const [search] = useQueryState("name", parseAsString);

  useEffect(() => {
    onSearch(search || "");
  }, [search]);

  useEffect(() => {
    onPaginationChange(page || 1, size || 5);
  }, [page, size]);

  useEffect(() => {
    if (_sort && _sort.length > 0) {
      onSort(_sort[0].id as ParticipantTableField, _sort[0].desc);
    }
  }, [_sort]);

  const [participantToRemove, setParticipantToRemove] =
    useState<Participant | null>(null);

  const columns: ColumnDef<Participant>[] = [
    {
      id: "user__name",
      header: ({ column }: { column: Column<Participant, unknown> }) => (
        <DataTableColumnHeader column={column} title="Name" className="px-6" />
      ),
      accessorKey: "user__name",
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ row }: { row: Row<Participant> }) => {
        return <div className="px-4">{renderUserCell(row)}</div>;
      },
    },
    ...(hasPendingUsers
      ? [
          {
            id: "pendingStatus",
            header: ({ column }: { column: Column<Participant, unknown> }) => (
              <DataTableColumnHeader column={column} title="Pending Status" />
            ),
            accessorKey: "pendingStatus",
          },
        ]
      : []),
    ...(isLead
      ? [
          {
            id: "actions",
            header: ({ column }: { column: Column<Participant, unknown> }) => (
              <div className="flex justify-end pr-6">
                <DataTableColumnHeader column={column} title="Actions" />
              </div>
            ),
            enableSorting: false,
            enableColumnFilter: false,

            cell: ({ row }: { row: Row<Participant> }) => {
              const { deleted, roles } = row.original || {};
              const isParticipant = roles?.includes(
                ProgramUserRole.Participant,
              );
              const canRemove =
                !deleted &&
                !isProgramClosed &&
                (isAdmin || (isParticipant && isLead)) &&
                !!onRemoveParticipant;
              return (
                <div className="flex justify-end pr-10">
                  {canRemove && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button intent="secondary" size="sm">
                          <MoreHorizontalIcon size={14} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          variant="destructive"
                          onClick={() => {
                            setParticipantToRemove(row.original);
                          }}
                        >
                          Remove Participant
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              );
            },
          },
        ]
      : []),
  ];

  const { table } = useDataTable({
    data: participants as Participant[],
    columns,
    pageCount,
    enableHiding: false,
    getRowId: (row) => row?.id || "",
    initialState: {
      pagination: {
        pageIndex: page || 1,
        pageSize: size || 5,
      },
    },
  });

  const loadingSkeleton = (
    <div className="flex flex-col gap-4 h-full w-full">
      {Array.from({ length: size || 5 }).map((_, index) => (
        <Skeleton key={index} className="h-10 w-full" />
      ))}
    </div>
  );

  return (
    <>
      <AlertDialog
        open={!!participantToRemove}
        onOpenChange={(open) => !open && setParticipantToRemove(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Participant</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this participant? This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setParticipantToRemove(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              intent="danger"
              onClick={() => {
                if (participantToRemove) {
                  onRemoveParticipant?.(participantToRemove);
                  setParticipantToRemove(null);
                }
              }}
            >
              Remove Participant
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <DataTable
        table={table}
        className="overflow-visible"
        totalRecords={totalRecords}
        emptyState={
          <div className="flex flex-col items-center justify-center py-8">
            {loading ? (
              loadingSkeleton
            ) : query ? (
              <div className="flex flex-col items-center justify-center text-gray-500">
                No participants found. Please try again with different search
                criteria.
              </div>
            ) : (
              <>
                <EmbedSvg src={NoParticipantsSvg} width={48} height={48} />
                <div className="my-4">No Participants Yet</div>
                {onAddParticipants && !isProgramClosed && (
                  <Button
                    intent="primary"
                    onClick={() => {
                      onAddParticipants();
                    }}
                  >
                    <Plus /> Add Participants
                  </Button>
                )}
              </>
            )}
          </div>
        }
      >
        <DataTableToolbar table={table}></DataTableToolbar>
      </DataTable>
    </>
  );
}
